/**
 * Public build-time configuration for the dashboard (Phase 8).
 *
 * The dashboard is a STATIC Cloudflare Pages app. It holds NO backend secrets —
 * only public `PUBLIC_*` values that are safe to ship in the client bundle:
 *   - PUBLIC_API_URL              base URL of the ODIN control API
 *   - PUBLIC_APP_ENV              environment label (development|staging|production)
 *   - PUBLIC_TURNSTILE_SITE_KEY   Cloudflare Turnstile SITE key (public, never the secret)
 *   - PUBLIC_STATUS_URL           public status endpoint (may be edge-cached / cross-origin)
 *   - PUBLIC_RELEASE_VERSION      release version / commit shown in the UI
 *   - PUBLIC_DEMO_MODE            "true" to present the app as a public demo (no real
 *                                 server/API connected) — shows a clear demo banner and
 *                                 replaces API-unreachable errors with a calm demo notice
 *
 * A Turnstile SITE key is public by design; the SECRET key lives ONLY on the
 * control-api and is never referenced here. Vite is configured (see vite.config.ts)
 * to expose the `PUBLIC_` prefix in addition to `VITE_`.
 */

type EnvBag = Record<string, string | undefined>;

function readEnv(...keys: string[]): string {
  const bag = import.meta.env as unknown as EnvBag;
  for (const key of keys) {
    const value = bag[key];
    if (typeof value === "string" && value.trim().length > 0) return value.trim();
  }
  return "";
}

/** Base URL of the control API. `PUBLIC_API_URL` is preferred; the legacy
 * `VITE_CONTROL_API_URL` is still honoured so older builds keep working. */
export const apiBaseUrl = readEnv("PUBLIC_API_URL", "VITE_CONTROL_API_URL");

/** Public status endpoint. Falls back to `${apiBaseUrl}/status` when unset. */
export const statusUrl = readEnv("PUBLIC_STATUS_URL") || (apiBaseUrl ? `${apiBaseUrl}/status` : "");

/** Environment label shown in the UI (never authoritative — the API reports its own). */
export const appEnv = readEnv("PUBLIC_APP_ENV") || "development";

/** Public Turnstile SITE key. Empty string means Turnstile is not configured. */
export const turnstileSiteKey = readEnv("PUBLIC_TURNSTILE_SITE_KEY");

/** Release version / commit for the dashboard build (display only). */
export const releaseVersion = readEnv("PUBLIC_RELEASE_VERSION") || "dev";

/**
 * Public demo mode. When `PUBLIC_DEMO_MODE` is "true" (or "1"), the dashboard is
 * presented as a public demo: a persistent banner makes clear that no real Discord
 * server or control API is connected, and the "can't reach the API" states become a
 * calm demo notice instead of an alarming error. This is display-only and never
 * fabricates live data — empty values still render honestly as "—".
 */
export const demoMode = ((): boolean => {
  const raw = readEnv("PUBLIC_DEMO_MODE").toLowerCase();
  return raw === "true" || raw === "1" || raw === "yes";
})();

export const config = {
  apiBaseUrl,
  statusUrl,
  appEnv,
  turnstileSiteKey,
  releaseVersion,
  demoMode,
} as const;
